'use client'

// Landing "Founders" section — puts a human face on the service to build
// trust before the visitor reaches pricing. Rendered between <Process />
// and <Plans /> on the landing.
//
// Bilingual (ES/EN). The language is now driven by the global trc-lang
// cookie via the one nav toggle: page.tsx reads the cookie and passes
// `lang` down as a prop, so this section stays in sync with the rest of
// the landing. The component keeps its own photo-zoom client state.
//
// Founders with a headshot show a circular photo that opens to a larger
// circle on click; the rest fall back to a monogram avatar. Initials are
// explicit (not derived) because Carlos Chaves and Christopher Campos
// would otherwise both render "CC".

import { useState, useEffect } from 'react'
import Image from 'next/image'

type Lang = 'en' | 'es'

const SECTION: Record<Lang, { eyebrow: string; headline: string; lead: string }> = {
  en: {
    eyebrow: 'Who we are',
    headline: 'A house, seen from every side.',
    lead:
      'The build, the upkeep, and the day you call with a problem. We cover all three, with one team and a written record of everything we touch.',
  },
  es: {
    eyebrow: 'Quiénes somos',
    headline: 'Una casa, vista desde cada lado.',
    lead:
      'La obra, el mantenimiento y el día que llamás con un problema. Cubrimos los tres, con un solo equipo y un registro escrito de todo lo que tocamos.',
  },
}

type Founder = {
  initials: string
  name: string
  /** Optional square headshot in /public. When set, the avatar shows the
   *  photo and opens a larger circle on click. */
  photo?: string
  en: { role: string; bio: string }
  es: { role: string; bio: string }
}

// Order mirrors the lead sentence: build → upkeep → service.
const FOUNDERS: Founder[] = [
  {
    initials: 'CA',
    name: 'Carlos Chaves',
    photo: '/founders/carlos.jpg',
    en: {
      role: 'Engineer',
      bio: 'A civil engineer whose construction firm, Nest, has delivered builds and projects across Nosara and the rest of the country. He knows first-hand the real problems an owner faces once the house is finally built.',
    },
    es: {
      role: 'Ingeniero',
      bio: 'Ingeniero con amplia experiencia en construcción. Su constructora, Nest, ha estado a cargo de obras y proyectos en Nosara y en distintas partes del país. Entiende de primera mano las dificultades reales que enfrenta un dueño una vez que su casa ya está construida.',
    },
  },
  {
    initials: 'CH',
    name: 'Christopher Campos',
    photo: '/founders/christopher.jpg',
    en: {
      role: 'Lead technician',
      bio: "Spent years caring for one of Nosara's finest properties inside a hospitality operation, focused on the finishes and on leaving every space better than he found it. He leads the technical team and signs off the property file.",
    },
    es: {
      role: 'Líder técnico',
      bio: 'Trabajó varios años en un proyecto de hospitalidad a cargo del mantenimiento y cuidado de una de las propiedades más lindas de Nosara, enfocado en los acabados y en que todo quede mejor de como lo encontró. Lidera el equipo técnico y firma el expediente de cada casa.',
    },
  },
  {
    initials: 'MB',
    name: 'Mauricio Barrantes',
    photo: '/founders/mauricio.jpg',
    en: {
      role: 'Customer Success',
      bio: 'With deep roots in hospitality, he built a system that puts the customer first, making life easy for every member, with close service and information that is always clear and within reach.',
    },
    es: {
      role: 'Customer Success',
      bio: 'Con bases sólidas en hospitalidad, construyó un sistema verdaderamente customer-centric: hacerle la vida fácil a quien contrata el servicio, con atención cercana e información siempre clara y a la mano.',
    },
  },
]

export default function LandingFounders({ lang }: { lang: Lang }) {
  const [zoom, setZoom] = useState<{ src: string; name: string } | null>(null)
  const t = SECTION[lang]

  // Close the photo zoom on Escape and lock body scroll while it's open.
  useEffect(() => {
    if (!zoom) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoom(null) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [zoom])

  return (
    <section className="landing-section" id="about">
      <div className="section-head" style={{ maxWidth: '760px' }}>
        <div className="section-divider" aria-hidden="true" />
        <span className="landing-label">{t.eyebrow}</span>
        <h2 className="landing-headline">{t.headline}</h2>
        <p className="landing-body" style={{ color: 'var(--trc-ash)' }}>
          {t.lead}
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {FOUNDERS.map((f, i) => {
          const c = f[lang]
          return (
            <article
              key={f.name}
              className="group relative rounded-xl border border-trc-ash-line bg-white p-7
                         transition-shadow duration-300 ease-out hover:shadow-calm-lift"
            >
              <div className="flex items-center justify-between">
                {f.photo ? (
                  <button
                    type="button"
                    onClick={() => setZoom({ src: f.photo!, name: f.name })}
                    aria-label={lang === 'es' ? `Ver foto de ${f.name}` : `View photo of ${f.name}`}
                    className="relative h-14 w-14 cursor-pointer overflow-hidden rounded-full
                               ring-2 ring-transparent transition-all duration-300
                               hover:ring-trc-blue/40 focus-visible:outline-none focus-visible:ring-trc-blue/60"
                  >
                    <Image src={f.photo} alt={f.name} fill sizes="56px" className="object-cover" />
                  </button>
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-trc-navy
                               text-[1.05rem] font-black tracking-tight text-trc-cream
                               ring-2 ring-transparent transition-all duration-300 group-hover:ring-trc-blue/30"
                  >
                    {f.initials}
                  </span>
                )}
                <span className="font-mono text-[0.78rem] font-bold tracking-label text-trc-blue">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              {c.role && (
                <div className="mt-5 text-label uppercase tracking-label text-trc-ash">
                  {c.role}
                </div>
              )}
              <h3 className={`${c.role ? 'mt-2' : 'mt-5'} text-[1.45rem] font-black leading-tight tracking-[-0.4px] text-trc-navy`}>
                {f.name}
              </h3>
              <p className="mt-4 text-[0.95rem] leading-[1.65] text-trc-navy/75">{c.bio}</p>
            </article>
          )
        })}
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-trc-navy/80 p-6 backdrop-blur-sm"
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label={zoom.name}
        >
          <div className="flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-72 w-72 overflow-hidden rounded-full shadow-2xl ring-4 ring-white/15 sm:h-80 sm:w-80">
              <Image src={zoom.src} alt={zoom.name} fill sizes="320px" className="object-cover" priority />
            </div>
            <span className="text-sm font-bold tracking-tight text-white">{zoom.name}</span>
            <button
              type="button"
              onClick={() => setZoom(null)}
              className="rounded-full border border-white/25 px-5 py-2 text-xs font-bold uppercase tracking-label text-white transition-colors hover:bg-white/10"
            >
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
